import type { RemoveUserFromOrganizationRequest } from '@compass/client/types/organization';
import type { SearchOrganizationUsersResponse } from '@compass/client/types/organization_user';
import type { InfiniteData } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys as orgUsersQueryKeys } from '@/hooks/useOrganizationUsers';
import { organizationService } from '@/services/api';

/**
 * Mutation hook for removing a user from an organization
 * This removes the user completely from the organization and all its programs
 */
export const useRemoveUserFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: RemoveUserFromOrganizationRequest) =>
      organizationService.removeUserFromOrganization(request),
    onSuccess: (_, variables) => {
      // Optimistically remove the user from cached organization user queries so the
      // list updates immediately. The cache now uses InfiniteData (from useInfiniteQuery)
      // so we need to update each page within the infinite data structure.
      const matchKey = [
        ...orgUsersQueryKeys.organizationUsers.all,
        'search',
        variables.organization_id,
      ];
      const queries = queryClient.getQueriesData<InfiniteData<SearchOrganizationUsersResponse>>({
        queryKey: matchKey,
      });
      for (const [key, data] of queries) {
        if (data?.pages) {
          queryClient.setQueryData<InfiniteData<SearchOrganizationUsersResponse>>(key, {
            ...data,
            pages: data.pages.map(page => ({
              ...page,
              users: page.users?.filter(u => u.user_id !== variables.user_id),
            })),
          });
        }
      }
    },
  });
};
